import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

/**
 * Admin login
 * TODO: Backend needs to implement: POST /api/admin/login
 * Request body: { email, password }
 * Expected response:
 * {
 *   "status": "success",
 *   "data": {
 *     "token": "admin_jwt_token",
 *     "admin": {
 *       "admin_id": "uuid",
 *       "email": "admin@realescy.com",
 *       "name": "Admin User",
 *       "role": "admin"
 *     }
 *   }
 * }
 */
export const adminLogin = createAsyncThunk('adminAuth/login', async ({ email, password }, { rejectWithValue }) => {
  try {
    const response = await axios.post('/api/admin/login', { email, password });

    if (response.data.status === 'success') {
      // Store token in localStorage
      localStorage.setItem('adminToken', response.data.data.token);
      return {
        token: response.data.data.token,
        admin: response.data.data.admin
      };
    }
    return rejectWithValue(response.data.message || 'Login failed');
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Login failed');
  }
});

/**
 * Admin logout
 * TODO: Backend may implement: POST /api/admin/logout
 */
export const adminLogout = createAsyncThunk('adminAuth/logout', async () => {
  try {
    // const { token } = getState().adminAuth;

    // Optional: Call backend logout endpoint
    // await axios.post('/api/admin/logout', {}, {
    //   headers: { Authorization: `Bearer ${token}` }
    // });

    // Clear token from localStorage
    localStorage.removeItem('adminToken');
    return null;
  } catch (error) {
    // Even if backend call fails, clear local token
    localStorage.removeItem('adminToken');
    return null;
  }
});

/**
 * Verify admin token on app load
 * TODO: Backend should implement: GET /api/admin/verify
 * Headers: Authorization: Bearer {admin_token}
 * Expected response:
 * {
 *   "status": "success",
 *   "data": {
 *     "admin": {
 *       "admin_id": "uuid",
 *       "email": "admin@realescy.com",
 *       "name": "Admin User",
 *       "role": "admin"
 *     }
 *   }
 * }
 */
export const verifyAdminToken = createAsyncThunk('adminAuth/verify', async (_, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem('adminToken');

    if (!token) {
      return rejectWithValue('No admin token found');
    }

    const response = await axios.get('/api/admin/verify', {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (response.data.status === 'success') {
      return {
        token,
        admin: response.data.data.admin
      };
    }

    localStorage.removeItem('adminToken');
    return rejectWithValue('Invalid token');
  } catch (error) {
    localStorage.removeItem('adminToken');
    return rejectWithValue('Token verification failed');
  }
});

const initialState = {
  token: null,
  isLoggedIn: false,
  isInitialized: false,
  admin: null,
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null
};

const adminAuthSlice = createSlice({
  name: 'adminAuth',
  initialState,
  reducers: {
    adminInitialize: (state, action) => {
      const { isLoggedIn, admin, token } = action.payload;
      state.isLoggedIn = isLoggedIn;
      state.isInitialized = true;
      state.token = token;
      state.admin = admin;
    },
    clearAdminError: (state) => {
      state.error = null;
    },
    adminLogoutLocal: (state) => {
      state.isLoggedIn = false;
      state.token = null;
      state.admin = null;
      localStorage.removeItem('adminToken');
    }
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(adminLogin.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(adminLogin.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.isLoggedIn = true;
        state.token = action.payload.token;
        state.admin = action.payload.admin;
        state.error = null;
      })
      .addCase(adminLogin.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // Logout
      .addCase(adminLogout.fulfilled, (state) => {
        state.isLoggedIn = false;
        state.token = null;
        state.admin = null;
        state.error = null;
      })

      // Verify token
      .addCase(verifyAdminToken.pending, (state) => {
        state.isInitialized = false;
      })
      .addCase(verifyAdminToken.fulfilled, (state, action) => {
        state.isInitialized = true;
        state.isLoggedIn = true;
        state.token = action.payload.token;
        state.admin = action.payload.admin;
      })
      .addCase(verifyAdminToken.rejected, (state) => {
        state.isInitialized = true;
        state.isLoggedIn = false;
        state.token = null;
        state.admin = null;
      });
  }
});

export const { adminInitialize, clearAdminError, adminLogoutLocal } = adminAuthSlice.actions;

// Selectors
export const selectIsAdminLoggedIn = (state) => state.adminAuth.isLoggedIn;
export const selectAdminToken = (state) => state.adminAuth.token;
export const selectAdmin = (state) => state.adminAuth.admin;

export default adminAuthSlice.reducer;
